import { useEffect, useState } from 'react';
import { Box, Chip, Paper, Skeleton, Stack, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';

import PageHeader from '../../../components/PageHeader/PageHeader';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
import useReportCurrency from '../../../hooks/useReportCurrency';
import { getCostAlertsData } from '../../../services/ReportsCostAlertsService';
import { formatMoney } from '../../../utils/currency';

const money = (value = 0) => `${formatMoney(Number(value || 0))}`;

const AlertCard = ({ alert }) => {
  const hasCostAndPrice = alert.cost !== null && alert.cost !== undefined && alert.price !== null && alert.price !== undefined;

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} useFlexGap flexWrap="wrap">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>{alert.title}</Typography>
          {alert.locationName && (
            <Typography variant="body2" color="text.secondary" noWrap>· {alert.locationName}</Typography>
          )}
        </Stack>
        {hasCostAndPrice && (
          <Typography variant="body2" color="text.secondary">
            Costo {money(alert.cost)} · Precio {money(alert.price)}
          </Typography>
        )}
      </Stack>

      {alert.chain.length > 1 && (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>Cadena hasta la causa probable</Typography>
          <Stack direction="row" alignItems="center" spacing={0.5} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
            {alert.chain.map((step, index) => (
              <Stack key={`${step.kind}-${step.label}-${index}`} direction="row" alignItems="center" spacing={0.5}>
                {index > 0 && <ChevronRightIcon fontSize="small" color="disabled" />}
                <Chip
                  size="small"
                  variant={index === alert.chain.length - 1 ? 'filled' : 'outlined'}
                  color={index === alert.chain.length - 1 ? 'error' : 'default'}
                  label={step.detail ? `${step.label} · ${step.detail}` : step.label}
                />
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>{alert.message}</Typography>
    </Paper>
  );
};

const CostAlertsReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      setLoading(true);
      try {
        const result = await getCostAlertsData();
        if (!ignore) setData(result);
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    fetchData();
    return () => { ignore = true; };
  }, [reportCurrency]);

  const alerts = data?.alerts || [];

  return (
    <Box p={3}>
      <PageHeader
        title="Alertas de costos"
        subtitle="Productos cuyo costo supera el precio de venta: probablemente estén mal cargados"
      />
      <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />

      <Stack spacing={1.5} sx={{ mt: 2, mb: 5 }}>
        {loading && [0, 1, 2].map((key) => <Skeleton key={key} variant="rectangular" height={96} sx={{ borderRadius: 1.5 }} />)}
        {!loading && alerts.length === 0 && (
          <Paper variant="outlined" sx={{ p: 4, borderRadius: 1.5 }}>
            <Stack alignItems="center" spacing={1}>
              <CheckCircleOutlineIcon color="success" fontSize="large" />
              <Typography variant="subtitle1" fontWeight={700}>No hay alertas de costos</Typography>
              <Typography variant="body2" color="text.secondary">Ningún producto tiene un costo mayor a su precio de venta.</Typography>
            </Stack>
          </Paper>
        )}
        {!loading && alerts.map((alert, index) => <AlertCard key={`${alert.id}-${index}`} alert={alert} />)}
      </Stack>
    </Box>
  );
};

export default CostAlertsReports;
